import { memo, useCallback, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import { useFormikContext } from "formik";
import { noop } from "neetocist";
import { FormikEditor } from "neetoeditor";
import { MenuHorizontal } from "neetoicons";
import { Modal, Typography, Dropdown } from "neetoui";
import { Textarea } from "neetoui/formik";
import PropTypes from "prop-types";
import { mergeRight } from "ramda";
import { useTranslation } from "react-i18next";

import { DEFAULT_EDITOR_PROPS, EDITOR_ADDONS } from "./constants";
import "./editor.scss";
import useEditor from "./hooks/useEditor";
import MediaUploader from "./MediaUploader";
import { getThumbnailAddon } from "./utils";

const Editor = ({
  attachments,
  onContentChange,
  onAttachmentChange,
  onTitleChange,
  getTitleRef = noop,
  getEditorRef = noop,
  menuClassName,
  editorContentFieldName = "htmlContent",
  titleFieldName = "title",
  editorProps: customEditorProps = {},
  titleProps = {},
  hasThumbnailImage = false,
  children,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, submitForm } = useFormikContext();
  const { thumbnailImageUrl } = values;

  const { Menu, MenuItem } = Dropdown;

  const titleRef = useRef();
  const editorRef = useRef();
  const [isThumbnailImageModalOpen, setIsThumbnailImageModalOpen] =
    useState(false);

  const editorProps = useMemo(() => {
    const baseProps = mergeRight(DEFAULT_EDITOR_PROPS, customEditorProps);

    if (!hasThumbnailImage) return baseProps;

    const thumbnailAddon = getThumbnailAddon(() =>
      setIsThumbnailImageModalOpen(true)
    );

    return mergeRight(baseProps, { addonCommands: [thumbnailAddon] });
  }, [customEditorProps, hasThumbnailImage]);

  const {
    handleContentChange,
    handleAttachmentChange,
    handleTitleChange,
    editorHotkeyRef,
    titleHotkeyRef,
    errors,
  } = useEditor({
    onContentChange,
    editorContentFieldName,
    onAttachmentChange,
    onTitleChange,
    titleFieldName,
    editorRef,
    titleRef,
  });

  const setTitleRef = node => {
    titleHotkeyRef.current = node;
    getTitleRef(node);
    titleRef.current = node;
  };

  const setEditorRef = useCallback(node => {
    editorRef.current = node;
    getEditorRef(node);
  }, []);

  const addons = useMemo(
    () => EDITOR_ADDONS.concat(editorProps.addons || []),
    [editorProps.addons]
  );

  const handleTab = () => {
    const isListActive =
      editorRef.current?.editor?.isActive("orderedList") ||
      editorRef.current?.editor?.isActive("bulletList");

    if (isListActive) {
      editorRef.current?.editor?.commands?.sinkListItem("listItem");

      return true;
    }

    return true;
  };

  const handleDeleteThumbnail = () => {
    setFieldValue("thumbnailImage", "");
    setFieldValue("thumbnailImageUrl", "");
    setTimeout(submitForm);
  };

  return (
    <div
      className="neeto-molecules-document__wrapper"
      data-testid="document-editor-container"
      ref={editorHotkeyRef}
    >
      <FormikEditor
        attachmentsClassName="editor-content-attachments w-full flex-shrink-0"
        className="h-full w-full"
        contentClassName="editor-content__wrapper neeto-molecules-document__container"
        data-testid="neeto-molecules-document-content-text-area"
        error={undefined}
        errorWrapperClassName="w-full min-h-full flex flex-col items-center"
        hideSlashCommands={false}
        name={editorContentFieldName}
        ref={setEditorRef}
        menuClassName={classNames(
          "neeto-molecules-document-editor__menubar",
          menuClassName
        )}
        {...{ attachments, ...editorProps, addons }}
        contentWrapperClassName="w-full flex flex-col flex-grow"
        keyboardShortcuts={{ Tab: handleTab, ...editorProps.keyboardShortcuts }}
        onChange={handleContentChange}
        onChangeAttachments={handleAttachmentChange}
      >
        {children}
        <div className="neeto-molecules-document__container px-6">
          <Textarea
            nakedTextarea
            className="neeto-molecules-document__title"
            data-testid="neeto-molecules-document-title-text-field"
            name={titleFieldName}
            ref={setTitleRef}
            rows={1}
            placeholder={
              titleProps.placeholder || t("neetoMolecules.documentEditor.title")
            }
            onChange={handleTitleChange}
            onKeyDown={e =>
              e.key === "Enter" && !e.shiftKey && e.preventDefault()
            }
            {...titleProps}
          />
          {thumbnailImageUrl && (
            <div className="group relative mt-4">
              <div className="neeto-molecules-document__thumbnail-image">
                <img alt="Cover" src={thumbnailImageUrl} />
              </div>
              <div className="neeto-ui-rounded-full absolute end-2 top-2">
                <Dropdown
                  strategy="fixed"
                  buttonProps={{
                    style: "secondary",
                    size: "small",
                    icon: MenuHorizontal,
                  }}
                >
                  <Menu>
                    <MenuItem.Button onClick={handleDeleteThumbnail}>
                      {t("neetoMolecules.documentEditor.delete")}
                    </MenuItem.Button>
                  </Menu>
                </Dropdown>
              </div>
            </div>
          )}
          {errors[editorContentFieldName] && (
            <Typography
              className="neeto-ui-input__error neeto-molecules-editor-content__error"
              data-testid="articles-content-input-error"
              style="body3"
            >
              {errors[editorContentFieldName]}
            </Typography>
          )}
        </div>
      </FormikEditor>
      <Modal
        className="media-uploader-modal p-5 pt-0"
        data-testid="thumbnail-image-modal"
        isOpen={isThumbnailImageModalOpen}
        onClose={() => {
          setIsThumbnailImageModalOpen(false);
        }}
      >
        <Modal.Header className="ms-0 ps-0">
          <Typography style="h2">
            {t("neetoMolecules.documentEditor.uploadThumbnailImage")}
          </Typography>
        </Modal.Header>
        <MediaUploader {...{ setIsThumbnailImageModalOpen }} />
      </Modal>
    </div>
  );
};

Editor.propTypes = {
  /**
   * List of initial attachments.
   */
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      contentType: PropTypes.string,
      filename: PropTypes.string,
      signedId: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  /**
   * Callback that will be triggered when the editor content changes.
   * This function is not throttled.
   */
  onContentChange: PropTypes.func,
  /**
   * Callback that will be triggered when the attachments changes.
   */
  onAttachmentChange: PropTypes.func,
  /**
   * Callback that will be triggered when the article title changes.
   */
  onTitleChange: PropTypes.func,
  /**
   * This function will be called with the title node reference.
   */
  getTitleRef: PropTypes.func,
  /**
   * This function will be called with editor reference.
   */
  getEditorRef: PropTypes.func,
  /**
   * Formik field name of the editor. The default value is `htmlContent`.
   */
  editorContentFieldName: PropTypes.string,
  /**
   * Formik field name of the title. The default value is `title`.
   */
  titleFieldName: PropTypes.string,
  /**
   * These props will be passed down to the editor component.
   */
  editorProps: PropTypes.object,
  /**
   * These props will be passed down to the title text area component.
   */
  titleProps: PropTypes.object,
  /**
   * A prop to pass class names to the editor menubar.
   */
  menuClassName: PropTypes.string,
  /**
   * The children will be rendered above the title input field.
   */
  children: PropTypes.node,
  /**
   * A prop to enable the thumbnail image uploader.
   */
  hasThumbnailImage: PropTypes.bool,
};

Editor.displayName = "DocumentEditor";

export default memo(Editor);
